import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, ClickAwayListener } from '@mui/material'
import { components } from 'react-select'

import TextFilter from 'components/TextFilter'
import Select from 'components/global/CUK/AsyncSelect/Select'
import { OptionMultiCount } from 'components/global/CUK/AsyncSelect/components/Option'
import StateIcon from 'components/global/StateIcon'

import { useFilter } from '../store'
import { DEVICE_STATUSES } from '../device-status'

const DropdownIndicator = ({ ...props }) => {
  return (
    <Box mr={1}>
      {props.selectProps.menuIsOpen ? (
        <StateIcon
          iconlabel={'angle-up'}
          iconweight={'regular'}
          iconcolor={'primary.main'}
        />
      ) : (
        <StateIcon iconweight={'regular'} iconlabel={'angle-down'} />
      )}
    </Box>
  )
}

const ValueContainer = ({ ...props }) => {
  const selectedValuesCount = props.getValue().length
  const { menuIsOpen } = props.selectProps
  const { t } = useTranslation()

  return (
    <components.ValueContainer {...props}>
      <Box>
        <Box
          sx={{
            color: menuIsOpen ? '#3B82F6' : 'grey.500',
            fontSize: '12px',
            paddingLeft: 1.5,
            fontWeight: 500,
          }}
        >
          {selectedValuesCount === 0
            ? props.selectProps.placeholder
            : t('stream-cast.filters.selected', { count: selectedValuesCount })}
        </Box>
      </Box>
    </components.ValueContainer>
  )
}

const Option = (props) => {
  return (
    <OptionMultiCount
      {...props}
      value={props.data}
      count={props.data.count}
      availability={props.data.value}
    />
  )
}

const MultiSelectFilter = ({ options, placeholder, values, onChange }) => (
  <Box width={200}>
    <Select
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      options={options}
      value={options.filter(option => values.includes(option.value))}
      mainField="label"
      placeholder={placeholder}
      onChange={selectedOptions => onChange((selectedOptions || []).map(option => option.value))}
      components={{
        Option,
        Control: ({ children, ...props }) => {
          const { menuIsOpen, onMenuClose, onMenuOpen } = props.selectProps
          return (
            <components.Control {...props}>
              <ClickAwayListener
                mouseEvent='onMouseDown'
                onClickAway={onMenuClose}
              >
                <Box
                  onClick={menuIsOpen ? onMenuClose : onMenuOpen}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexGrow: 1,
                    gap: 7,
                    cursor: 'pointer'
                  }}
                >
                  {children}
                </Box>
              </ClickAwayListener>
            </components.Control>
          )
        },
        DropdownIndicator,
        ValueContainer,
        IndicatorSeparator: null,
        ClearIndicator: null
      }}
    />
  </Box>
)

const Filters = ({ counts }) => {
  const { t } = useTranslation()
  const [filter, setFilter] = useFilter()

  const typeOptions = [
    {
      label: t('stream-cast.filters.tv'),
      value: 'tv',
      count: counts.tv,
      id: 'tv'
    },
    {
      label: t('stream-cast.filters.chromecast'),
      value: 'chromecast',
      count: counts.chromecast,
      id: 'chromecast'
    }
  ]

  const statusOptions = [
    {
      label: t('stream-cast.filters.operational'),
      value: DEVICE_STATUSES.OPERATIONAL,
      count: counts.operational,
      id: DEVICE_STATUSES.OPERATIONAL
    },
    {
      label: t('stream-cast.filters.unreachable'),
      value: DEVICE_STATUSES.UNREACHABLE,
      count: counts.unreachable,
      id: DEVICE_STATUSES.UNREACHABLE
    },
    {
      label: t('stream-cast.filters.not_configured'),
      value: DEVICE_STATUSES.NOT_CONFIGURED,
      count: counts.not_configured,
      id: DEVICE_STATUSES.NOT_CONFIGURED
    }
  ]

  return (
    <Box display="flex" gap={2}>
      <TextFilter
        value={filter.verbatim}
        placeholder={t('stream-cast.filters.search')}
        onChange={(value) => setFilter((prev) => ({ ...prev, verbatim: value }))}
      />
      <MultiSelectFilter
        options={typeOptions}
        placeholder={t('stream-cast.filters.types')}
        values={filter.deviceTypes}
        onChange={(deviceTypes) => setFilter(prev => ({ ...prev, deviceTypes }))}
      />
      <MultiSelectFilter
        options={statusOptions}
        placeholder={t('stream-cast.filters.status')}
        values={filter.statuses}
        onChange={(statuses) => setFilter(prev => ({ ...prev, statuses }))}
      />
    </Box>
  )
}

export default Filters
